import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  Output,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DropdownOption } from '../simple-dropdown/simple-dropdown.component';

@Component({
  selector: 'app-simple-autocomplete',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './simple-autocomplete.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./simple-autocomplete.component.scss'],
})
export class SimpleAutocompleteComponent {
  @Input() options: DropdownOption[] = [];
  @Input() selectedValue = '';
  @Input() placeholder = 'Search...';
  @Input() disabled = false;

  @Output() selectionChange = new EventEmitter<string>();

  isOpen = false;
  isFocused = false;
  searchQuery = '';

  constructor(private elementRef: ElementRef) {}

  get filteredOptions(): DropdownOption[] {
    const query = this.searchQuery.trim().toLowerCase();
    if (!query) {
      return this.options;
    }
    return this.options.filter(option => option.label.toLowerCase().includes(query));
  }

  get inputValue(): string {
    return this.isOpen ? this.searchQuery : this.getSelectedLabel();
  }

  get showClearButton(): boolean {
    return this.isFocused && this.inputValue.trim().length > 0;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    if (!this.elementRef.nativeElement.contains(event.target)) {
      this.closePanel();
    }
  }

  onInputFocus(): void {
    if (this.disabled) {
      return;
    }
    this.isFocused = true;
    this.isOpen = true;
    this.searchQuery = this.selectedValue ? this.getSelectedLabel() : '';
  }

  onInputBlur(): void {
    this.isFocused = false;
  }

  onInputChange(value: string): void {
    this.searchQuery = value;
    this.isOpen = true;
  }

  onClear(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    this.searchQuery = '';
    this.selectionChange.emit('');
    this.isOpen = true;
    this.isFocused = true;
  }

  selectOption(value: string): void {
    this.closePanel();
    this.selectionChange.emit(value);
  }

  getSelectedLabel(): string {
    if (!this.selectedValue) {
      return '';
    }
    const option = this.options.find(opt => opt.value === this.selectedValue);
    return option ? option.label : '';
  }

  trackByValue(_: number, item: DropdownOption): string {
    return item.value;
  }

  private closePanel(): void {
    this.isOpen = false;
    this.isFocused = false;
    this.searchQuery = '';
  }
}
